import { useEffect, useRef, useState } from 'react';

import { CheckmarkCircle03Icon } from '@hugeicons/core-free-icons';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';

import { AnimatedModal } from 'components/AnimatedModal/AnimatedModal';
import { Button } from 'components/FormComponents';
import { Checkbox } from 'components/FormComponents/Checkbox/Checkbox';

export const UserConsentModal = ({
  consents,
  visible,
  className,
  onSubmit = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const [consentsData, setConsentsData] = useState(consents ?? []);
  const prevVisibleRef = useRef(false);

  // Reset/sync state when modal opens so checkboxes are unchecked and consents prop is respected
  useEffect(() => {
    if (visible && !prevVisibleRef.current && consents) {
      setConsentsData(consents.map(c => ({ ...c, checked: false })));
    }
    prevVisibleRef.current = visible;
  }, [visible, consents]);

  const handleConsentChange = consentId => {
    setConsentsData(prevConsents =>
      prevConsents.map(consent =>
        consent.id === consentId ? { ...consent, checked: !consent.checked } : consent,
      ),
    );
  };

  const allConsentsChecked =
    (consentsData?.length ?? 0) > 0 && consentsData.every(consent => consent.checked);

  const handleSubmit = () => {
    if (!allConsentsChecked) return;
    const consents = consentsData
      .filter(consent => consent.checked)
      .map(consent => {
        return {
          key: consent.id,
          version: consent.version,
          text: consent.content,
        };
      });
    onSubmit(consents);
  };

  return (
    <AnimatedModal
      visible={visible}
      onClose={onClose}
      title={t('check.consent.userConsentsTitle')}
      small
      icon={CheckmarkCircle03Icon}
      oldIconLibrary={false}>
      <div className={cn({ [className]: className })}>
        {(consentsData || []).map(consent => (
          <div key={consent.id}>
            {consent.title && (
              <span className="primary-text u-font-weight-500 u-font-size-small">
                {consent.title}
              </span>
            )}
            <Checkbox
              align="start"
              label={
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(consent.content ?? '', {
                      ALLOWED_TAGS: ['div', 'span', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'br'],
                      ALLOWED_ATTR: [],
                    }),
                  }}
                />
              }
              checked={consent.checked}
              onChange={() => handleConsentChange(consent.id)}
            />
          </div>
        ))}
        <Button onClick={handleSubmit} className="u-width-100" disabled={!allConsentsChecked}>
          Launch Check
        </Button>
      </div>
    </AnimatedModal>
  );
};
